/**
 * Per-tenant terminology lexicon.
 *
 * Two halves:
 *  - TERMS: the renameable nouns. Tenants override these (their config is merged
 *    over these defaults at bootstrap). A term carries its `gender` and its
 *    pre-composed article/preposition `forms`. Locale files reference a form with
 *    the token `{term:form}` (e.g. `{opportunity:the_singular}`).
 *  - AGREEING: words that must agree with whichever term they sit next to
 *    (adjectives, past participles, pronouns). NOT tenant-overridable — shared
 *    system vocabulary. Locale files reference one with `{word|term}` or
 *    `{word|term:plural}` (e.g. `{active|opportunity}`). The resolver reads the
 *    bound term's gender + the requested number and picks ms/fs/mp/fp.
 *
 * Defaults are authored so that, with no tenant overlay, every migrated string
 * renders byte-identical to its pre-refactor value.
 */

export const TERMS = {
  'fr-CA': {
    opportunity: {
      gender: 'f',
      // Begins with a vowel sound (vowel or mute-h). Drives pre-posed adjective elision
      // (nouveau -> nouvel); article elision is already baked into the forms below.
      startsWithVowelSound: true,
      forms: {
        singular: 'opportunité',
        plural: 'opportunités',
        the_singular: "l'opportunité",
        the_plural: 'les opportunités',
        a_singular: 'une opportunité',
        a_plural: 'des opportunités',
        this_singular: 'cette opportunité',
        this_plural: 'ces opportunités',
        of_singular: "d'opportunité",
        of_plural: "d'opportunités",
        of_the_singular: "de l'opportunité",
        of_the_plural: 'des opportunités',
        to_the_singular: "à l'opportunité",
        to_the_plural: 'aux opportunités',
        no_singular: 'aucune opportunité',
      },
    },
  },
  'en-CA': {
    opportunity: {
      gender: 'n',
      startsWithVowelSound: true,
      forms: {
        singular: 'opportunity',
        plural: 'opportunities',
        the_singular: 'the opportunity',
        the_plural: 'the opportunities',
        a_singular: 'an opportunity',
        a_plural: 'opportunities',
        this_singular: 'this opportunity',
        this_plural: 'these opportunities',
        of_singular: 'of opportunity',
        of_plural: 'of opportunities',
        of_the_singular: 'of the opportunity',
        of_the_plural: 'of the opportunities',
        to_the_singular: 'to the opportunity',
        to_the_plural: 'to the opportunities',
        no_singular: 'no opportunity',
      },
    },
  },
};

// Agreement forms keyed ms (masc-sing) / fs (fem-sing) / mp (masc-plur) / fp (fem-plur).
// Optional `msv` = masc-sing before a vowel sound (nouveau -> nouvel); only the few elidable
// pre-posed adjectives declare it, everything else falls back to `ms` (see pickAgreementForm).
export const AGREEING = {
  'fr-CA': {
    active: { ms: 'actif', fs: 'active', mp: 'actifs', fp: 'actives' },
    external: { ms: 'externe', fs: 'externe', mp: 'externes', fp: 'externes' },
    shared: { ms: 'partagé', fs: 'partagée', mp: 'partagés', fp: 'partagées' },
    synced: { ms: 'synchronisé', fs: 'synchronisée', mp: 'synchronisés', fp: 'synchronisées' },
    created: { ms: 'créé', fs: 'créée', mp: 'créés', fp: 'créées' },
    future: { ms: 'futur', fs: 'future', mp: 'futurs', fp: 'futures' },
    deleted: { ms: 'supprimé', fs: 'supprimée', mp: 'supprimés', fp: 'supprimées' },
    similar: { ms: 'similaire', fs: 'similaire', mp: 'similaires', fp: 'similaires' },
    new: { ms: 'nouveau', msv: 'nouvel', fs: 'nouvelle', mp: 'nouveaux', fp: 'nouvelles' },
    linked: { ms: 'lié', fs: 'liée', mp: 'liés', fp: 'liées' },
    parent: { ms: 'parent', fs: 'parente', mp: 'parents', fp: 'parentes' },
    eligible: { ms: 'admissible', fs: 'admissible', mp: 'admissibles', fp: 'admissibles' },
    updated: { ms: 'mis à jour', fs: 'mise à jour', mp: 'mis à jour', fp: 'mises à jour' },
    // pronoun ("it" / "they") — same token mechanism, agrees like an adjective
    it: { ms: 'il', fs: 'elle', mp: 'ils', fp: 'elles' },
  },
  'en-CA': {
    active: { ms: 'active', fs: 'active', mp: 'active', fp: 'active' },
    external: { ms: 'external', fs: 'external', mp: 'external', fp: 'external' },
    shared: { ms: 'shared', fs: 'shared', mp: 'shared', fp: 'shared' },
    synced: { ms: 'synced', fs: 'synced', mp: 'synced', fp: 'synced' },
    created: { ms: 'created', fs: 'created', mp: 'created', fp: 'created' },
    future: { ms: 'future', fs: 'future', mp: 'future', fp: 'future' },
    deleted: { ms: 'deleted', fs: 'deleted', mp: 'deleted', fp: 'deleted' },
    similar: { ms: 'similar', fs: 'similar', mp: 'similar', fp: 'similar' },
    new: { ms: 'new', fs: 'new', mp: 'new', fp: 'new' },
    linked: { ms: 'linked', fs: 'linked', mp: 'linked', fp: 'linked' },
    parent: { ms: 'parent', fs: 'parent', mp: 'parent', fp: 'parent' },
    eligible: { ms: 'eligible', fs: 'eligible', mp: 'eligible', fp: 'eligible' },
    updated: { ms: 'updated', fs: 'updated', mp: 'updated', fp: 'updated' },
    it: { ms: 'it', fs: 'it', mp: 'they', fp: 'they' },
  },
};
